<template>
  <div>
    <b-container>
      <CheckinSummary/>
      <div class="gallery-tree-content">
        <div v-if="error" class="alert alert-danger">
          {{ error }}
        </div>
        <div v-else-if="!loading">
          <div v-if="hasMedias" class="mt-4">
            <media-displayer
                :gallery="gallery"
                @openGallery="handleOpenGallery"
            />
          </div>
          <div v-else>
            <UserCtas :hide-user-cta="hideUserCta"
                      @face-checkin-cta="openFaceRecognitionModal()"/>
          </div>
        </div>
      </div>


      <ModalShowQrPicacode :picacode="picaCode"/>
      <ModalAnonymizeCheckin :checkin-id="checkin.id"
                             :checkin-anonymize-status="checkin.anonymize_status"
                             :checkin-event-name="eventName"
                             :checkin-anonymize-mode="checkin.anonymize_mode"
                             ref="modalAnonymizeCheckin"
                             @anonymize-call-back="onAnonymizeCallBack"/>
      <ModalRemoveCheckin :checkin-id="checkin.id"
                          :picacode="checkin.picacode"/>
      <ModalFaceInference
          :checkin-id="checkin.id"
          @face-inference-started="onFaceInferenceStarted"
      />
      <ModalDownloadAlbumChoice :gallery-id="albumGalleryId"/>
      <ModalCurrencyMismatch />
    </b-container>
    <div class="mt-2 mb-4">
      <StaticBanner :pica-service-id="picaServiceId" :banner="eventBanner"/>
      <WelcomePopup :pica-service-id="picaServiceId" :active="eventPopup.welcome.active === true"
                    :title="eventPopup.welcome.title" :text="eventPopup.welcome.text"/>
      <FixedPopup :pica-service-id="picaServiceId" :active="eventPopup.fixed.active === true"
                  :title="eventPopup.fixed.title" :text="eventPopup.fixed.text"/>

    </div>

    <ShareWithFriends :data="shareWithFriends" :event-name="eventName" v-if="shareWithFriends.enabled"/>
  </div>

</template>

<script>
import {mapActions, mapGetters, mapMutations} from 'vuex'
import MediaDisplayer from './MediaDisplayer'
import CheckinSummary from "@/domain/galleryTree/components/CheckinSummary.vue";
import UserCtas from "@/domain/galleries/components/UserCtas.vue";
import ModalAnonymizeCheckin from "@/components/ModalAnonymizeCheckin.vue";
import Gallery from "@/domain/metrics/gallery";
import ModalRemoveCheckin from "@/components/ModalRemoveCheckin.vue";
import ModalFaceInference from "@/components/ModalFaceInference.vue";
import ModalShowQrPicacode from "@/components/ModalShowQrPicacode.vue";
import ModalDownloadAlbumChoice from "@/components/ModalDownloadAlbumChoice.vue";
import WelcomePopup from "@/components/customEvents/WelcomePopup.vue";
import StaticBanner from "@/components/customEvents/StaticBanner.vue";
import ShareWithFriends from "@/domain/checkinDetail/ShareWithFriends.vue";
import FixedPopup from "@/components/customEvents/FixedPopup.vue";
import ModalCurrencyMismatch from "@/domain/galleries/modals/ModalCurrencyMismatch.vue";

export default {
  name: 'Checkin',

  components: {
    ModalCurrencyMismatch,
    FixedPopup, ShareWithFriends, StaticBanner, WelcomePopup,
    ModalShowQrPicacode,
    ModalFaceInference,
    ModalRemoveCheckin,
    ModalDownloadAlbumChoice,
    ModalAnonymizeCheckin,
    UserCtas,
    CheckinSummary,
    MediaDisplayer
  },

  data() {
    return {
      loading: false,
      error: null,
      galleryData: null,
      hideUserCta: false,
      timerStarted: false,
      anonymizing: false,
    }
  },

  computed: {
    ...mapGetters('checkinDetail', [
      'checkin',
      'picaServiceId',
      'picaCode',
      'eventName',
      'isTaggingCompleted',
      'eventBanner',
      'eventPopup',
      'shareWithFriends',
      'useGetPicaForYourEvent',
    ]),
    ...mapGetters('galleryTree', [
      'gallery',
      'media',
      'priceList',
      'rootGalleryId'
    ]),
    picacode() {
      return this.$route.params.picacode
    },
    galleryId() {
      return this.$route.params.gallery_id
    },
    hasMedias() {
      return this.gallery.num_media>0 || this.gallery.sub_gallery.some(g => g.num_media > 0)
    },
    // At the gallery-tree root rootGalleryId is 0 (the "whole tree" sentinel):
    // send no gallery_id so the backend downloads the whole album. Only a real
    // sub-gallery id (>0) scopes the download.
    albumGalleryId() {
      const id = Number(this.rootGalleryId)
      return Number.isFinite(id) && id > 0 ? id : null
    }
  },

  methods: {
    ...mapActions('galleryTree', [
      'setCheckinId',
      'setRootGalleryId',
      'fetchGallery',
      'fetchMedia',
      'fetchPriceList',
      'clearGalleryTree'
    ]),
    ...mapActions('checkin', [
      'openAddSelfieStep'
    ]),
    ...mapMutations('event', ['SET_PICASERVICE_ID', 'UNSET_PICASERVICE_ID']),
    ...mapActions('checkinDetail', [
      'unsetEventPopup'
    ]),
    ...mapActions('bannerGetPica', [
      'setCheckinSettings',
      'exitCheckin'
    ]),

    async fetchData() {
      this.loading = true
      this.error = null

      try {
        // Set the checkin ID from the checkinDetail store
        await this.setCheckinId(this.checkin.id)
        const newGallery = await this.setRootGalleryId(this.galleryId)
        if (!newGallery) {
          this.loading = false
          return
        }

        await this.fetchGallery()

        this.loading = false
        await this.$nextTick()
        Gallery.enterInAlbum(this.picaServiceId)
        this.timerStarted = true

        this.setCheckinSettings(this.useGetPicaForYourEvent)
      } catch (err) {
        this.error = err.message || 'An error occurred while fetching data';
        this.loading = false;
      }
    },

    handleOpenGallery(galleryId) {
      // Update the URL with the new gallery ID
      this.$router.push({
        name: this.$route.name,
        params: {
          ...this.$route.params,
          gallery_id: galleryId
        }
      })
    },

    openFaceRecognitionModal(recognitionFallbackMode = false) {
      this.openAddSelfieStep({
        checkin: {id: this.checkin.id, picacode: this.checkin.picacode},
        max_faces_count: this.checkin.max_faces_count,
        checkin_faces: this.checkin.checkin_faces,
        user_cta: this.checkin.user_cta,
        liveness_detection_enabled: this.checkin.liveness_detection_enabled,
        stay_mode: null,
        face_checkin_privacy_statement: null,
        callback: (checkinId, newCheckinFaces, faceCheckinComplete = false) => {
          if (checkinId === this.checkin.id) {
            this.$set(this.checkin, 'checkin_faces', newCheckinFaces)
          }

          if (recognitionFallbackMode && faceCheckinComplete) {
            this.hideUserCta = true
          }

          if (this.anonymizing) {
            this.$refs.modalAnonymizeCheckin.takeAction()
            this.anonymizing = false
          }
        }
      })
    },

    onAnonymizeCallBack({anonymizeStatus, checkinFaceMissing}) {
      if (anonymizeStatus) {
        this.checkin.anonymize_status = anonymizeStatus
      }

      if (checkinFaceMissing) {
        this.anonymizing = true
        this.openFaceRecognitionModal();
      }
    },
    onFaceInferenceStarted() {
      // Refresh galleries after face inference is started
      if (this.$refs.galleries) {
        setTimeout(() => {
          this.fetchData()
        }, 2000)
      }
    },
  },

  created() {
    this.fetchData()
  },

  async mounted() {
    this.SET_PICASERVICE_ID(this.checkin.pica_service_id)

    if (this.$route.params.openAnonymize) {
      await this.wait(600)
      this.$bvModal.show('modal-anonymize-checkin')
    }
  },

  beforeDestroy() {
    this.exitCheckin()
    this.unsetEventPopup()
    if (this.timerStarted) Gallery.exitAlbum()
    this.UNSET_PICASERVICE_ID()
    this.clearGalleryTree()
  },

  watch: {
    '$route.params': {
      handler() {
        this.fetchData()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.gallery-tree-content {
  min-height: 1000px;
}
</style>
